
import { Component, OnInit, ViewChild, Input, OnChanges, AfterViewInit, Inject, Output, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogTitle, MatDialogContent, MatDialogActions, MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { NgForm, UntypedFormBuilder, UntypedFormGroup, Validators, AbstractControl, FormControl, UntypedFormArray } from '@angular/forms';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AlertService, MessageSeverity } from '../../../services/alert.service';
import { AppTranslationService } from '../../../services/app-translation.service';
import { AspectTextViewModel, AspectTextService, ReportMetadataService, AspectTextBusinessCategoryViewModel, Int32StringTuple } from '../../index';
import { AuthService } from '../../../services/auth.service';
import { Permission } from '../../../models/permission.model';
import { Utilities } from '../../../services/utilities';
import { DateFormatterPipe } from '../../../pipes/dateformatter.pipe';

@Component({
    selector: 'app-aspect-text-details',
    templateUrl: './aspect-text-details.component.html',
    styleUrls: ['./aspect-text-details.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AspectTextDetailsComponent implements OnInit {
    @ViewChild('form', { static: true }) form: NgForm;

    aspectTextForm: UntypedFormGroup;
    aspectText: AspectTextViewModel;
    businessCategories: Int32StringTuple[];

    aspectTextId: number = null;
    aspectId: number;
    aspectName: string;

    loadingIndicator: boolean = false;
    public isNew = false;
    private isSaving: boolean = false;
    anySaveOperationsPerformed: boolean = false;

    createdByName$: Observable<string>;
    changedByName$: Observable<string>;


    constructor(
        public dialogRef: MatDialogRef<AspectTextDetailsComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any,
        private alertService: AlertService,
        private translationService: AppTranslationService,
        private authService: AuthService,
        private aspectTextService: AspectTextService,
        private reportMetadataService: ReportMetadataService,
        private formBuilder: UntypedFormBuilder,
        private snackBar: MatSnackBar,
        public dialog: MatDialog
    ) {
        this.aspectTextId = data.aspectTextId;
        this.aspectId = data.aspectId;
        this.aspectName = data.aspectName;
    }

    ngOnInit(): void {
        this.buildForm();

        if (this.aspectTextId != null) {
            this.loadData(this.aspectTextId);
        } else {
            this.isNew = true;
            this.loadBusinessCategories();
        }

        // Use this construct to handle the escape key for cancelling
        this.dialogRef.keydownEvents().pipe(map(r => new EventMapper(r, this))).subscribe((e) => {
                if (e.event.key === "Escape") {
                    e.component.cancel();
                }
            }
        );

    }

    loadBusinessCategories(): void {
        this.reportMetadataService.getBusinessCategoryMetadata().subscribe(t => this.businessCategoriesLoaded(t),
            error => this.onDataLoadFailed(error));
    }

    businessCategoriesLoaded(businessCategories: Int32StringTuple[]): void {
        this.businessCategories = businessCategories;

        this.resetForm();
    }


    public loadData(aspectTextId: number): void {
        this.alertService.startLoadingMessage();
        this.loadingIndicator = true;
        this.aspectTextService.getAspectTextById(aspectTextId).subscribe(ok => this.dataLoaded(ok),
            error => this.onDataLoadFailed(error));
    }

    dataLoaded(aspectText: AspectTextViewModel) {
        this.alertService.stopLoadingMessage();
        this.loadingIndicator = false;
        this.aspectText = aspectText;
        this.isNew = false;
        this.resetForm();
    }

    private onDataLoadFailed(error: any) {
        this.alertService.stopLoadingMessage();
        this.loadingIndicator = false;

        this.alertService.showStickyMessage(this.translationService.getTranslation("Error"),
            this.translationService.getTranslation("FetchErrorMessage") +
            "\r\n" +
            Utilities.getHttpResponseMessage(error),
            MessageSeverity.error,
            error);
    }


    public getFormControl(name: string): AbstractControl {
        return this.aspectTextForm.controls[name];
    }

    get businessCategoriesArray(): UntypedFormArray {
        return this.aspectTextForm.get('businessCategories') as UntypedFormArray;
    }


    private buildForm() {
        this.aspectTextForm = this.formBuilder.group({
            aspectTextId: '',
            name: ['', [Validators.required, Validators.maxLength(100)]],
            enabled: '',
            aspectId: '',
            sortOrder: '',
            versionNo: '',
            scopeDescription: '',
            guidelinesDescription: '',
            actionsRequiredDescription: '',
            acceptableDescription: '',
            goodDescription: '',
            veryGoodDescription: '',
            excellentDescription: '',
            outstandingDescription: '',
            createdBy: '',
            createdDate: '',
            updatedBy: '',
            updatedDate: '',
            businessCategories: this.formBuilder.array([])
        });
    }

    public resetForm() {
        if (!this.aspectText) {
            this.isNew = true;
            this.aspectText = this.initializeEmptyObject();
        }

        this.aspectTextForm.reset({
            aspectTextId: this.aspectText.aspectTextId || '',
            name: this.aspectText.name || '',
            enabled: this.aspectText.enabled || '',
            aspectId: this.aspectText.aspectId || '',
            sortOrder: this.aspectText.sortOrder || '',
            versionNo: this.aspectText.versionNo || '',
            scopeDescription: this.aspectText.scopeDescription || '',
            guidelinesDescription: this.aspectText.guidelinesDescription || '',
            actionsRequiredDescription: this.aspectText.actionsRequiredDescription || '',
            acceptableDescription: this.aspectText.acceptableDescription || '',
            goodDescription: this.aspectText.goodDescription || '',
            veryGoodDescription: this.aspectText.veryGoodDescription || '',
            excellentDescription: this.aspectText.excellentDescription || '',
            outstandingDescription: this.aspectText.outstandingDescription || '',
            createdBy: this.aspectText.createdBy || '',
            createdDate: new DateFormatterPipe().transform(this.aspectText.createdDate, true) || '',
            updatedBy: this.aspectText.updatedBy || '',
            updatedDate: new DateFormatterPipe().transform(this.aspectText.updatedDate, true) || '',
        });

        let businessCategoriesFormGroups = this.aspectText.businessCategories.map(cat => this.formBuilder.group(
            {
                businessCategoryId: cat.businessCategoryId || '',
                businessCategoryName: cat.businessCategoryName || '',
                selected: cat.selected || false,
            }));
        let businessCategoryFormArray = this.formBuilder.array(businessCategoriesFormGroups);
        this.aspectTextForm.setControl('businessCategories', businessCategoryFormArray);

        if (this.aspectText.createdBy) {
            this.createdByName$ = this.reportMetadataService.getUserById(this.aspectText.createdBy).pipe(map(t => t.item2));
        }
        if (this.aspectText.updatedBy) {
            this.changedByName$ = this.reportMetadataService.getUserById(this.aspectText.updatedBy).pipe(map(t => t.item2));
        }
    }

    private initializeEmptyObject(): AspectTextViewModel {
        var result = <AspectTextViewModel>{};
        result.aspectId = this.aspectId;
        result.enabled = true;
        result.versionNo = 1;

        let aspectTextBusinessCategories: AspectTextBusinessCategoryViewModel[] = [];

        this.businessCategories.forEach(t => aspectTextBusinessCategories.push(
            {
                businessCategoryId: t.item1,
                businessCategoryName: t.item2,
                selected: false
            }
        ));

        result.businessCategories = aspectTextBusinessCategories;
        return result;
    }

    private prepareSave(): AspectTextViewModel {
        const formModel = this.aspectTextForm.value;

        let aspectText : AspectTextViewModel = {
            aspectTextId: this.isNew ? null : formModel.aspectTextId,
            name: formModel.name,
            enabled: formModel.enabled,
            aspectId: formModel.aspectId,
            sortOrder: formModel.sortOrder,
            versionNo: formModel.versionNo,
            scopeDescription: formModel.scopeDescription,
            guidelinesDescription: formModel.guidelinesDescription,
            actionsRequiredDescription: formModel.actionsRequiredDescription,
            acceptableDescription: formModel.acceptableDescription,
            goodDescription: formModel.goodDescription,
            veryGoodDescription: formModel.veryGoodDescription,
            excellentDescription: formModel.excellentDescription,
            outstandingDescription: formModel.outstandingDescription
        };

        let businessCategories: AspectTextBusinessCategoryViewModel[] = formModel.businessCategories;
        aspectText.businessCategories = businessCategories;

        return aspectText;
    }

    public save(close: boolean) {
        if (!this.aspectTextForm.valid) {
            this.alertService.showValidationError();
            return;
        }

        this.isSaving = true;
        this.alertService.startLoadingMessage(this.translationService.getTranslation("globalTexts.SavingMessage"));

        const aspectText = this.prepareSave();

        if (this.isNew) {
            this.aspectTextService.createAspectText(aspectText).subscribe(
                t => this.saveCompleted(close, t),
                error => this.saveFailed(error));
        } else {
            this.aspectTextService.updateAspectText(this.aspectTextId, aspectText).subscribe(
                t => this.saveCompleted(close, t),
                error => this.saveFailed(error));
        }
    }

    public cancel() {
        this.alertService.resetStickyMessage();

        if (this.aspectTextForm.dirty) {
            this.snackBar.open(this.translationService.getTranslation("globalTexts.DirtyFormBackQuery"),
                this.translationService.getTranslation("commands.OK"),
                { duration: 5000 })
                .onAction().subscribe(() => {
                    this.dialogRef.close(this.anySaveOperationsPerformed);
                }
                );
        } else {
            this.dialogRef.close(this.anySaveOperationsPerformed);
        }
    }

    private saveCompleted(close: boolean, aspectText: AspectTextViewModel) {
        this.aspectText = aspectText;
        this.aspectTextId = aspectText.aspectTextId;
        this.isSaving = false;
        this.isNew = false;
        this.anySaveOperationsPerformed = true;
        this.alertService.stopLoadingMessage();
        this.alertService.showMessage(this.translationService.getTranslation("commands.Save"), aspectText.name + " " + this.translationService.getTranslation("globalTexts.saved"), MessageSeverity.success);
        if (close) {
            this.dialogRef.close(true);
        } else {
            this.resetForm();
        }
    }

    private saveFailed(error: any) {
        this.isSaving = false;
        this.alertService.stopLoadingMessage();
        this.alertService.showStickyMessage(this.translationService.getTranslation("globalTexts.CouldNotSave"), this.translationService.getTranslation("globalTexts.SaveErrorMessage"), MessageSeverity.error, error);
        this.alertService.showStickyMessage(error, null, MessageSeverity.error);
    }

    hasPermissionToChange(): boolean {
        return this.authService.userHasPermission(Permission.manageModel);
    }

}

export class EventMapper {
    component: AspectTextDetailsComponent;
    event: KeyboardEvent;

    constructor(event: KeyboardEvent, component: AspectTextDetailsComponent) {
        this.component = component;
        this.event = event;
    }
}
